import { API_CONFIG } from '../constants';
import { getToken, clearToken, parseErrorMessage, redirectToLogin } from '../utils';

// Remove trailing slash if present to avoid double slashes in paths
const getApiBaseUrl = (): string => {
  const url = API_CONFIG.BASE_URL;
  return url.replace(/\/+$/, '');
};

export const API_BASE_URL = getApiBaseUrl();

/**
 * Simple GET request helper
 */
export async function simpleGet<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    headers: {
      'Accept': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
}

/**
 * Simple POST request helper (no authentication)
 */
export async function simplePost(url: string, body: object): Promise<Response> {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(body),
  });
  return response;
}

/**
 * Authenticated fetch wrapper
 * Automatically redirects to login on authentication errors
 */
export async function authenticatedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = getToken();
  
  if (!token) {
    // Brak tokenu - wyczyść i przekieruj
    clearToken();
    redirectToLogin();
    throw new Error('No token available');
  }

  const headers = new Headers(options.headers);
  headers.set('Authorization', `Bearer ${token}`);

  // Default response type expected by most endpoints.
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');

  const body = options.body;
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  const hasBody = body !== undefined && body !== null;

  /**
   * If we send JSON, ensure Content-Type is set (unless caller already did).
   * If we send FormData, DO NOT set Content-Type — the browser must add the multipart boundary.
   */
  if (isFormData) {
    headers.delete('Content-Type');
  } else if (hasBody && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  // Dodaj domyślny timeout jeśli nie ma AbortSignal
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout
  
  const signal = options.signal || controller.signal;

  try {
    const response = await fetch(url, { ...options, headers, signal });

    if (response.status === 401) {
      // Sesja wygasła - wyczyść token i przekieruj
      clearToken();
      redirectToLogin();
      throw new Error('User session expired');
    }

    return response;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Auth request helper (login/register)
 */
export async function authRequest<T>(url: string, body: object): Promise<T> {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(body),
    redirect: 'follow',
  });

  if (!response.ok) {
    throw new Error(await parseErrorMessage(response));
  }
  
  return response.json();
}

/**
 * Authenticated API call helper
 */
export async function authenticatedApiCall<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await authenticatedFetch(url, options);
  if (!response.ok) {
    throw new Error(await parseErrorMessage(response));
  }
  return response.json();
}

